<template>
    <section class="dp-layout assign-page">
        <section class="dp-left">
            <div class="assign-pane-head">
                <div>
                    <div class="assign-pane-head__title">角色</div>
                    <div class="assign-pane-head__sub">共 {{ filteredRoles.length }} 个</div>
                </div>
                <el-button type="text" icon="el-icon-refresh" :loading="loadingRoles" @click="handleQuery">刷新</el-button>
            </div>
            <div class="assign-search">
                <el-input v-model="roleKeyword" size="small" clearable prefix-icon="el-icon-search"
                    placeholder="搜索角色名称" />
            </div>
            <div v-loading="loadingRoles" class="assign-role-list">
                <button v-for="role in filteredRoles" :key="role.Id" type="button" class="role-item"
                    :class="{ 'is-active': role.Id === roleId }" :title="role.Name" @click="operate(role.Id)">
                    <span class="role-item__avatar">{{ roleInitial(role.Name) }}</span>
                    <span class="role-item__meta">
                        <span class="role-item__name">{{ role.Name }}</span>
                        <span v-if="role.Description" class="role-item__desc">{{ role.Description }}</span>
                    </span>
                    <el-tag v-if="role.Enabled === false" size="mini" type="info">停用</el-tag>
                </button>
                <el-empty v-if="!loadingRoles && !filteredRoles.length" :image-size="64"
                    :description="roleKeyword ? '没有匹配的角色，试试其他关键词' : '暂无角色'" />
            </div>
        </section>

        <section class="dp-right">
            <div class="assign-toolbar">
                <div class="assign-toolbar__main">
                    <div class="assign-current">
                        <div class="assign-current__name">{{ currentRoleName || '未选择角色' }}</div>
                        <div class="assign-current__meta">
                            <template v-if="roleId">
                                已选 <b>{{ selectedCount }}</b> 项
                                <span v-if="isDirty" class="assign-dirty">未保存</span>
                            </template>
                            <template v-else>请先在左侧选择要配置的角色</template>
                        </div>
                    </div>
                    <div class="assign-toolbar__actions">
                        <el-button size="small" :disabled="!roleId || !data5.length" @click="expandAll">展开全部</el-button>
                        <el-button size="small" :disabled="!openModuleIds.length" @click="collapseAll">收起全部</el-button>
                        <el-button size="small" :disabled="!roleId" @click="expandGranted">展开已授权</el-button>
                        <el-button type="primary" size="small" icon="el-icon-check" :loading="loadingSave"
                            :disabled="!roleId" @click="saveAssign">保存</el-button>
                    </div>
                </div>
                <div class="assign-toolbar__filters">
                    <el-input v-model="menuKeyword" size="small" clearable prefix-icon="el-icon-search"
                        placeholder="搜索菜单或操作，如「订餐」「导出」" class="assign-menu-search" />
                    <div class="assign-filter-group">
                        <span v-if="grantFilter !== 'all' && filterChangedCount > 0" class="assign-filter-hint">
                            已变更 {{ filterChangedCount }} 项
                            <el-button type="text" size="mini" icon="el-icon-refresh" @click="refreshGrantFilter">刷新视图</el-button>
                        </span>
                        <el-radio-group v-model="grantFilter" size="small">
                            <el-radio-button label="all" @click.native="onGrantFilterBtnClick('all')">全部</el-radio-button>
                            <el-radio-button label="on" @click.native="onGrantFilterBtnClick('on')">已勾选</el-radio-button>
                            <el-radio-button label="off" @click.native="onGrantFilterBtnClick('off')">未勾选</el-radio-button>
                        </el-radio-group>
                    </div>
                </div>
            </div>

            <div v-loading="loadingTree || loadingAssign" class="assign-workspace"
                element-loading-text="加载中...">
                <el-empty v-if="!roleId && !loadingTree" :image-size="80" description="从左侧选择角色后，按模块勾选菜单和操作权限" />
                <el-empty v-else-if="roleId && !visibleModules.length && !loadingTree && !loadingAssign"
                    :image-size="80" :description="emptyMenuHint" />
                <div v-else-if="roleId" class="assign-modules">
                    <div v-for="mod in visibleModules" :key="mod.value" class="mod-card"
                        :class="{ 'is-open': isModuleOpen(mod.value), 'is-leaf': !hasChildren(mod) }">
                        <div class="mod-head" @click="toggleModuleOpen(mod)">
                            <el-checkbox :value="isMenuChecked(mod.value)"
                                :indeterminate="moduleStat(mod).indeterminate"
                                @click.native.stop @change="val => onModuleCheck(mod, val)" />
                            <span class="mod-head__title">{{ mod.label }}</span>
                            <span class="mod-head__count">{{ moduleStat(mod).checked }}/{{ moduleStat(mod).total }}</span>
                            <div v-if="mod.btns && mod.btns.length" class="perm-chips" @click.stop>
                                <button v-for="btn in mod.btns" :key="btn.value" type="button" class="perm-chip"
                                    :class="{ 'is-on': isBtnChecked(btn.value) }" @click="toggleBtn(btn.value)">
                                    {{ btn.label }}
                                </button>
                            </div>
                            <div class="mod-head__ops" @click.stop>
                                <button type="button" class="mod-link" @click="setTreeChecked(mod, true)">全选</button>
                                <button type="button" class="mod-link" @click="setTreeChecked(mod, false)">清空</button>
                                <i v-if="hasChildren(mod)" class="el-icon-arrow-right mod-head__arrow"></i>
                            </div>
                        </div>
                        <div v-if="hasChildren(mod) && isModuleOpen(mod.value)" class="mod-body">
                            <div v-for="row in flattenNodes(mod.children, 0)" :key="row.value" class="perm-row"
                                :style="{ paddingLeft: (12 + row.depth * 18) + 'px' }">
                                <el-checkbox :value="isMenuChecked(row.value)"
                                    @change="val => setMenuChecked(row.raw, val)" />
                                <span class="perm-name" :title="row.label">{{ row.label }}</span>
                                <div class="perm-chips">
                                    <button v-for="btn in row.btns" :key="btn.value" type="button" class="perm-chip"
                                        :class="{ 'is-on': isBtnChecked(btn.value) }" @click="toggleBtn(btn.value)">
                                        {{ btn.label }}
                                    </button>
                                    <button v-if="row.btns.length > 1" type="button" class="perm-chip perm-chip--ghost"
                                        @click="toggleAllBtns(row.raw)">
                                        {{ allBtnsChecked(row.raw) ? '清空操作' : '全选操作' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </section>
</template>

<script>
import { getRoleListPage, getPermissionTree, getPermissionIds, addRolePermission } from '../../api/modules/system';

export default {
    name: 'PermissionAssign',
    data() {
        return {
            roles: [],
            roleId: 0,
            roleKeyword: '',
            data5: [],
            menuIdSet: {},
            checkedMenuIds: [],
            assignBtns: [],
            menuKeyword: '',
            grantFilter: 'all',
            /** 授权筛选基准快照（避免在已勾选/未勾选视图操作时项实时消失）。创建于 2026-09-06 */
            grantFilterBaseline: null,
            openModuleIds: [],
            savedSnapshot: '',
            loadingRoles: false,
            loadingTree: false,
            loadingAssign: false,
            loadingSave: false
        };
    },
    computed: {
        filteredRoles() {
            const kw = (this.roleKeyword || '').trim().toLowerCase();
            if (!kw) return this.roles;
            return this.roles.filter(r => (r.Name || '').toLowerCase().indexOf(kw) > -1
                || (r.Description || '').toLowerCase().indexOf(kw) > -1);
        },
        currentRoleName() {
            const role = this.roles.find(r => r.Id === this.roleId);
            return role ? role.Name : '';
        },
        selectedCount() {
            return this.checkedMenuIds.length + this.assignBtns.length;
        },
        isDirty() {
            return !!this.roleId && this.serializeAssign() !== this.savedSnapshot;
        },
        /**
         * 当前筛选模式下，已发生状态变更的权限项数量。创建于 2026-09-06
         */
        filterChangedCount() {
            if (this.grantFilter === 'all' || !this.grantFilterBaseline) return 0;
            let count = 0;
            const baselineMenuMap = this.grantFilterBaseline.menuMap || {};
            const baselineBtnMap = this.grantFilterBaseline.btnMap || {};

            if (this.grantFilter === 'off') {
                // 原本未勾选，当前已被勾上
                this.checkedMenuIds.forEach(id => {
                    if (!baselineMenuMap[this.toNum(id)]) count += 1;
                });
                this.assignBtns.forEach(id => {
                    if (!baselineBtnMap[this.toStr(id)]) count += 1;
                });
            } else if (this.grantFilter === 'on') {
                // 原本已勾选，当前已被取消
                Object.keys(baselineMenuMap).forEach(id => {
                    if (this.checkedMenuIds.indexOf(this.toNum(id)) === -1) count += 1;
                });
                Object.keys(baselineBtnMap).forEach(id => {
                    if (this.assignBtns.indexOf(this.toStr(id)) === -1) count += 1;
                });
            }
            return count;
        },
        visibleModules() {
            const kw = (this.menuKeyword || '').trim().toLowerCase();
            return this.filterNodes(this.data5, kw, this.grantFilter, false);
        },
        emptyMenuHint() {
            if (this.menuKeyword || this.grantFilter !== 'all') {
                return '没有匹配的菜单，试试其他关键词或切换筛选';
            }
            return '暂无菜单数据';
        }
    },
    watch: {
        menuKeyword() {
            this.autoExpandFiltered();
        },
        grantFilter() {
            this.updateGrantFilterBaseline();
            this.autoExpandFiltered();
        }
    },
    mounted() {
        this.handleQuery();
    },
    methods: {
        /**
         * 角色名称首字，用于左侧头像。最后修改：2026-08-26
         */
        roleInitial(name) {
            const text = (name || '').trim();
            return text ? text.charAt(0) : '角';
        },
        toNum(id) {
            return Number(id);
        },
        toStr(id) {
            return String(id);
        },
        hasChildren(node) {
            return !!(node && node.children && node.children.length);
        },
        isModuleOpen(id) {
            return this.openModuleIds.indexOf(this.toNum(id)) > -1;
        },
        /**
         * 拉取角色列表与菜单树
         */
        handleQuery() {
            this.loadingRoles = true;
            getRoleListPage({ page: -1 }).then((res) => {
                this.roles = (res.data.response && res.data.response.data) || [];
                this.getPermissions();
            }).catch(() => {
                this.loadingRoles = false;
            });
        },
        /**
         * 获取可分配的菜单树（按钮挂在 btns 上）
         */
        getPermissions() {
            this.loadingTree = true;
            getPermissionTree({ needbtn: false }).then((res) => {
                const children = (res.data.response && res.data.response.children) || [];
                this.data5 = children;
                const menuIdSet = {};
                this.collectMenuIds(children, menuIdSet);
                this.menuIdSet = menuIdSet;
            }).finally(() => {
                this.loadingRoles = false;
                this.loadingTree = false;
            });
        },
        collectMenuIds(nodes, set) {
            (nodes || []).forEach(node => {
                set[this.toNum(node.value)] = true;
                this.collectMenuIds(node.children, set);
            });
        },
        /**
         * 按角色回填已分配权限
         */
        getPermissionIds(rid) {
            this.loadingAssign = true;
            this.checkedMenuIds = [];
            this.assignBtns = [];
            getPermissionIds({ rid: rid }).then((res) => {
                const payload = (res.data && res.data.response) || {};
                const permissionIds = (payload.permissionids || []).map(id => this.toNum(id));
                this.checkedMenuIds = permissionIds.filter(id => this.menuIdSet[id]);
                this.assignBtns = (payload.assignbtns || []).map(id => this.toStr(id));
                this.captureSnapshot();
                if (this.grantFilter !== 'all') {
                    this.updateGrantFilterBaseline();
                }
            }).finally(() => {
                this.loadingAssign = false;
                this.loadingSave = false;
            });
        },
        /**
         * 切换角色；有未保存改动时先确认
         */
        operate(id) {
            if (id === this.roleId) return;
            const next = () => {
                this.roleId = id;
                this.openModuleIds = [];
                this.menuKeyword = '';
                this.grantFilter = 'all';
                this.grantFilterBaseline = null;
                this.loadingSave = false;
                this.getPermissionIds(id);
            };
            if (this.isDirty) {
                this.$confirm('当前修改尚未保存，是否放弃并切换角色？', '提示', {
                    type: 'warning',
                    confirmButtonText: '放弃并切换',
                    cancelButtonText: '取消'
                }).then(next).catch(() => {});
                return;
            }
            next();
        },
        isMenuChecked(id) {
            return this.checkedMenuIds.indexOf(this.toNum(id)) > -1;
        },
        isBtnChecked(id) {
            return this.assignBtns.indexOf(this.toStr(id)) > -1;
        },
        setMenuChecked(node, checked) {
            const id = this.toNum(node.value);
            const idx = this.checkedMenuIds.indexOf(id);
            if (checked && idx === -1) {
                this.checkedMenuIds.push(id);
                // 勾选子菜单时同步勾上祖先，否则侧栏导航树拼不出父节点。最后更新：2026-09-03
                this.ensureAncestorMenusChecked(node);
            }
            if (!checked && idx > -1) {
                this.checkedMenuIds.splice(idx, 1);
                this.clearNodeBtns(node);
            }
        },
        /**
         * 根据菜单树补齐祖先菜单 Id。创建于 2026-09-03
         */
        ensureAncestorMenusChecked(node) {
            const target = this.toNum(node && node.value);
            if (!target) return;
            const path = this.findMenuPath(this.data5, target);
            path.forEach((item) => {
                const id = this.toNum(item.value);
                if (id > 0 && this.checkedMenuIds.indexOf(id) === -1) {
                    this.checkedMenuIds.push(id);
                }
            });
        },
        /**
         * 查找某菜单从根到自身的路径。创建于 2026-09-03
         */
        findMenuPath(nodes, targetId, trail) {
            const path = trail || [];
            for (let i = 0; i < (nodes || []).length; i += 1) {
                const node = nodes[i];
                const next = path.concat([node]);
                if (this.toNum(node.value) === this.toNum(targetId)) return next;
                const found = this.findMenuPath(node.children, targetId, next);
                if (found.length) return found;
            }
            return [];
        },
        /**
         * 保存前确保所有已勾选菜单的祖先也在列表中。创建于 2026-09-03
         */
        collectMenuIdsWithAncestors(menuIds) {
            const result = [];
            const seen = {};
            (menuIds || []).forEach((rawId) => {
                const id = this.toNum(rawId);
                if (!id || seen[id]) return;
                const path = this.findMenuPath(this.data5, id);
                const ids = path.length ? path.map((n) => this.toNum(n.value)) : [id];
                ids.forEach((pid) => {
                    if (!pid || seen[pid]) return;
                    seen[pid] = true;
                    result.push(pid);
                });
            });
            return result;
        },
        toggleBtn(id) {
            const key = this.toStr(id);
            const idx = this.assignBtns.indexOf(key);
            if (idx > -1) {
                this.assignBtns.splice(idx, 1);
            } else {
                this.assignBtns.push(key);
            }
        },
        clearNodeBtns(node) {
            (node.btns || []).forEach(btn => {
                const idx = this.assignBtns.indexOf(this.toStr(btn.value));
                if (idx > -1) this.assignBtns.splice(idx, 1);
            });
        },
        allBtnsChecked(node) {
            const btns = (node && node.btns) || [];
            return btns.length > 0 && btns.every(btn => this.isBtnChecked(btn.value));
        },
        /**
         * 切换当前页全部操作权限
         */
        toggleAllBtns(node) {
            const turnOn = !this.allBtnsChecked(node);
            (node.btns || []).forEach(btn => {
                const key = this.toStr(btn.value);
                const idx = this.assignBtns.indexOf(key);
                if (turnOn && idx === -1) this.assignBtns.push(key);
                if (!turnOn && idx > -1) this.assignBtns.splice(idx, 1);
            });
        },
        walkNode(node, fn) {
            fn(node);
            (node.children || []).forEach(child => this.walkNode(child, fn));
        },
        /**
         * 全选/清空某个模块及其子级
         */
        setTreeChecked(node, checked) {
            this.walkNode(node, (item) => {
                this.setMenuChecked(item, checked);
                if (checked) {
                    (item.btns || []).forEach(btn => {
                        const key = this.toStr(btn.value);
                        if (this.assignBtns.indexOf(key) === -1) this.assignBtns.push(key);
                    });
                }
            });
        },
        onModuleCheck(mod, checked) {
            this.setTreeChecked(mod, checked);
        },
        moduleStats(node) {
            let total = 0;
            let checked = 0;
            this.walkNode(node, (item) => {
                total += 1;
                if (this.isMenuChecked(item.value)) checked += 1;
                (item.btns || []).forEach(btn => {
                    total += 1;
                    if (this.isBtnChecked(btn.value)) checked += 1;
                });
            });
            return { total, checked };
        },
        moduleStat(mod) {
            const stats = this.moduleStats(mod);
            return {
                total: stats.total,
                checked: stats.checked,
                indeterminate: stats.checked > 0 && stats.checked < stats.total
            };
        },
        toggleModuleOpen(mod) {
            if (!this.hasChildren(mod)) return;
            const id = this.toNum(mod.value);
            const idx = this.openModuleIds.indexOf(id);
            if (idx > -1) {
                this.openModuleIds.splice(idx, 1);
            } else {
                this.openModuleIds.push(id);
            }
        },
        expandAll() {
            this.openModuleIds = this.visibleModules
                .filter(mod => this.hasChildren(mod))
                .map(mod => this.toNum(mod.value));
        },
        collapseAll() {
            this.openModuleIds = [];
        },
        /**
         * 只展开当前有勾选的模块，便于核对已授权项
         */
        expandGranted() {
            this.openModuleIds = this.visibleModules
                .filter(mod => this.hasChildren(mod) && this.moduleStats(mod).checked > 0)
                .map(mod => this.toNum(mod.value));
        },
        autoExpandFiltered() {
            this.$nextTick(() => {
                const shouldExpand = !!(this.menuKeyword && this.menuKeyword.trim()) || this.grantFilter !== 'all';
                if (shouldExpand) this.expandAll();
            });
        },
        flattenNodes(nodes, depth) {
            const rows = [];
            (nodes || []).forEach(node => {
                rows.push({
                    value: node.value,
                    label: node.label,
                    btns: node.btns || [],
                    depth: depth,
                    raw: node
                });
                if (node.children && node.children.length) {
                    rows.push.apply(rows, this.flattenNodes(node.children, depth + 1));
                }
            });
            return rows;
        },
        labelMatches(node, kw) {
            if (!kw) return true;
            if ((node.label || '').toLowerCase().indexOf(kw) > -1) return true;
            return (node.btns || []).some(btn => (btn.label || '').toLowerCase().indexOf(kw) > -1);
        },
        /**
         * 菜单在筛选基准中是否处于勾选状态。创建于 2026-09-06
         */
        isMenuCheckedInBaseline(id) {
            if (!this.grantFilterBaseline) return this.isMenuChecked(id);
            return !!(this.grantFilterBaseline.menuMap && this.grantFilterBaseline.menuMap[this.toNum(id)]);
        },
        /**
         * 按钮在筛选基准中是否处于勾选状态。创建于 2026-09-06
         */
        isBtnCheckedInBaseline(id) {
            if (!this.grantFilterBaseline) return this.isBtnChecked(id);
            return !!(this.grantFilterBaseline.btnMap && this.grantFilterBaseline.btnMap[this.toStr(id)]);
        },
        /**
         * 更新已勾选/未勾选筛选的基准快照。
         * 避免在当前视图勾选/取消勾选时，节点因条件突变立刻从屏幕消失。
         * 创建于 2026-09-06
         */
        updateGrantFilterBaseline() {
            if (this.grantFilter === 'all') {
                this.grantFilterBaseline = null;
                return;
            }
            const menuMap = {};
            const btnMap = {};
            (this.checkedMenuIds || []).forEach(id => {
                menuMap[this.toNum(id)] = true;
            });
            (this.assignBtns || []).forEach(id => {
                btnMap[this.toStr(id)] = true;
            });
            this.grantFilterBaseline = { menuMap, btnMap };
        },
        /**
         * 手动重新应用筛选，按最新勾选状态刷新列表。创建于 2026-09-06
         */
        refreshGrantFilter() {
            this.updateGrantFilterBaseline();
            this.autoExpandFiltered();
            this.$message({ message: '已按当前权限状态刷新视图', type: 'info', duration: 1500 });
        },
        /**
         * 点击筛选单选按钮。重复点击当前项时触发刷新。创建于 2026-09-06
         */
        onGrantFilterBtnClick(label) {
            if (this.grantFilter === label && label !== 'all') {
                this.refreshGrantFilter();
            }
        },
        selfMatchesGrant(node, grant) {
            if (grant === 'all') return true;
            const menuOn = this.isMenuCheckedInBaseline(node.value);
            const btns = node.btns || [];
            const onCount = btns.filter(btn => this.isBtnCheckedInBaseline(btn.value)).length;
            if (grant === 'on') return menuOn || onCount > 0;
            return !menuOn || (btns.length > 0 && onCount < btns.length);
        },
        /**
         * 按关键词与授权状态裁剪菜单树，祖先匹配时子级仍可见
         */
        filterNodes(nodes, kw, grant, ancestorKwMatched) {
            const result = [];
            (nodes || []).forEach(node => {
                const kwOk = ancestorKwMatched || this.labelMatches(node, kw);
                const children = this.filterNodes(node.children, kw, grant, kwOk);
                const grantOk = this.selfMatchesGrant(node, grant);
                if (!kwOk && !children.length) return;
                if (grant !== 'all' && !grantOk && !children.length) return;
                result.push(Object.assign({}, node, {
                    children: children.length ? children : null
                }));
            });
            return result;
        },
        serializeAssign() {
            const menus = this.checkedMenuIds.slice().map(id => this.toNum(id)).sort((a, b) => a - b).join(',');
            const btns = this.assignBtns.slice().map(id => this.toStr(id)).sort().join(',');
            return menus + '|' + btns;
        },
        captureSnapshot() {
            this.savedSnapshot = this.serializeAssign();
        },
        /**
         * 保存当前角色的菜单与按钮权限
         */
        saveAssign() {
            if (!(this.roleId > 0)) {
                this.$message({ message: '请先选择要操作的角色', type: 'error' });
                return;
            }
            // 保存时强制补齐祖先菜单，避免只勾子页导致登录后侧栏无入口。最后更新：2026-09-03
            const pids = this.collectMenuIdsWithAncestors(this.checkedMenuIds);
            this.assignBtns.forEach(id => {
                const num = this.toNum(id);
                if (num > 0) pids.push(num);
            });
            this.loadingSave = true;
            addRolePermission({ pids: pids, rid: this.roleId }).then((res) => {
                if (res.data.success) {
                    this.$message({ message: res.data.msg || '保存成功', type: 'success' });
                    this.getPermissionIds(this.roleId);
                } else {
                    this.loadingSave = false;
                    this.$message({ message: res.data.msg || '保存失败', type: 'error' });
                }
            }).catch(() => {
                this.loadingSave = false;
            });
        }
    }
};
</script>

<style scoped>
.assign-page {
    --assign-text: #172033;
    --assign-muted: #526071;
    --assign-faint: #8b98a8;
    --assign-border: #dce5ef;
    --assign-soft: #f4f7fb;
    --assign-primary: #2563eb;
    --assign-primary-soft: #eff6ff;
}

.assign-page > .dp-right {
    overflow-y: hidden;
}

.assign-page > .dp-left {
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.dp-left {
    overflow: hidden;
}

.assign-pane-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px 16px 8px;
    flex: 0 0 auto;
}

.assign-pane-head__title {
    color: var(--assign-text);
    font-size: 15px;
    font-weight: 600;
    line-height: 1.4;
}

.assign-pane-head__sub {
    margin-top: 2px;
    color: var(--assign-faint);
    font-size: 12px;
}

.assign-search {
    padding: 0 16px 12px;
    flex: 0 0 auto;
}

.assign-role-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: 0 10px 12px;
}

.role-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    margin-bottom: 4px;
    padding: 8px 10px;
    border: 1px solid transparent;
    border-radius: 10px;
    background: transparent;
    color: var(--assign-text);
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;
}

.role-item:hover {
    background: var(--assign-soft);
}

.role-item.is-active {
    border-color: #bfdbfe;
    background: var(--assign-primary-soft);
}

.role-item__avatar {
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #e2e8f0;
    color: var(--assign-muted);
    font-size: 12px;
    font-weight: 600;
    line-height: 28px;
    text-align: center;
}

.role-item.is-active .role-item__avatar {
    background: var(--assign-primary);
    color: #fff;
}

.role-item__meta {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.role-item__name,
.perm-name,
.mod-head__title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.role-item__name {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
}

.role-item__desc {
    color: var(--assign-faint);
    font-size: 12px;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.assign-toolbar {
    flex: 0 0 auto;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--assign-border);
}

.assign-toolbar__main,
.assign-toolbar__filters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.assign-toolbar__filters {
    margin-top: 12px;
}

.assign-filter-group {
    display: flex;
    align-items: center;
    gap: 10px;
}

.assign-filter-hint {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--assign-muted);
    background: #f1f5f9;
    padding: 2px 8px;
    border-radius: 6px;
}

.assign-current__name {
    color: var(--assign-text);
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
}

.assign-current__meta {
    margin-top: 2px;
    color: var(--assign-muted);
    font-size: 12px;
}

.assign-current__meta b {
    color: var(--assign-primary);
    font-weight: 600;
}

.assign-dirty {
    margin-left: 8px;
    padding: 1px 6px;
    border-radius: 999px;
    background: #fef3c7;
    color: #b45309;
}

.assign-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
}

.assign-menu-search {
    width: 280px;
    max-width: 100%;
}

.department-config-page > .dp-right {
    overflow-y: hidden;
}

.assign-modules {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 8px;
}

.mod-card {
    border: 1px solid var(--assign-border);
    border-radius: 10px;
    background: #fff;
    overflow: hidden;
}

.mod-card.is-open {
    border-color: #bfdbfe;
    box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.08);
}

.mod-head {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    padding: 8px 12px;
    background: var(--assign-soft);
    cursor: pointer;
}

.mod-card.is-leaf .mod-head {
    cursor: default;
    background: #fff;
}

.mod-head__title {
    flex: 0 1 180px;
    color: var(--assign-text);
    font-size: 13px;
    font-weight: 600;
}

.mod-head__count {
    flex: 0 0 auto;
    color: var(--assign-muted);
    font-size: 12px;
    font-variant-numeric: tabular-nums;
}

.mod-head__ops {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.mod-link {
    padding: 0;
    border: 0;
    background: none;
    color: var(--assign-primary);
    font-size: 12px;
    cursor: pointer;
}

.mod-link:hover {
    text-decoration: underline;
}

.mod-head__arrow {
    color: var(--assign-faint);
    transition: transform 0.2s ease;
}

.mod-card.is-open .mod-head__arrow {
    transform: rotate(90deg);
}

.mod-body {
    border-top: 1px solid var(--assign-border);
}

.perm-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid #f1f5f9;
}

.perm-row:last-child {
    border-bottom: 0;
}

.perm-row:hover {
    background: #f8fafc;
}

.perm-name {
    flex: 0 0 160px;
    width: 160px;
    padding-top: 2px;
    color: var(--assign-text);
    font-size: 13px;
    line-height: 22px;
}

.perm-chips {
    flex: 0 1 auto;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
}

.perm-chip {
    height: 22px;
    padding: 0 8px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #fff;
    color: var(--assign-muted);
    font-size: 12px;
    line-height: 20px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.perm-chip:hover {
    border-color: #93c5fd;
    color: var(--assign-primary);
}

.perm-chip.is-on {
    border-color: var(--assign-primary);
    background: var(--assign-primary);
    color: #fff;
}

.perm-chip--ghost {
    border-style: dashed;
    color: var(--assign-faint);
}

.assign-page >>> .el-checkbox__label {
    display: none;
}

.assign-page >>> .el-empty {
    padding: 48px 0;
}

@media (prefers-reduced-motion: reduce) {
    .role-item,
    .perm-chip,
    .mod-head__arrow {
        transition: none;
    }
}

@media screen and (max-width: 992px) {
    .assign-toolbar__main,
    .assign-toolbar__filters {
        flex-wrap: wrap;
        align-items: stretch;
    }

    .assign-toolbar__actions {
        justify-content: flex-start;
    }

    .assign-menu-search {
        width: 100%;
    }

    .mod-head {
        flex-wrap: wrap;
    }

    .mod-head__title,
    .perm-name {
        flex: 1 1 100%;
        width: auto;
        max-width: none;
    }

    .mod-head__ops {
        width: 100%;
        margin-left: 0;
    }
}
</style>
